import { useState } from 'react';
import { AlertTriangle, RefreshCw, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { cn } from '@/lib/utils';

interface ErrorRecoveryBannerProps {
  taskId: string;
  errorCode: string;
  errorMessage: string;
  onResume: (taskId: string) => Promise<void>;
  onDismiss?: () => void;
  className?: string;
}

const ERROR_MESSAGES: Record<string, { title: string; description: string }> = {
  '429': {
    title: 'Rate Limit Exceeded',
    description: 'The API is temporarily rate limited. Please wait a moment before resuming.',
  },
  '529': {
    title: 'Service Overloaded',
    description: 'The Claude API is currently overloaded. Please try again shortly.',
  },
  '500': {
    title: 'Server Error',
    description: 'An internal server error occurred. You can try resuming the task.',
  },
  '502': {
    title: 'Bad Gateway',
    description: 'A gateway error occurred. This is usually temporary - try resuming.',
  },
  '503': {
    title: 'Service Unavailable',
    description: 'The service is temporarily unavailable. Please try again shortly.',
  },
};

export function ErrorRecoveryBanner({
  taskId,
  errorCode,
  errorMessage,
  onResume,
  onDismiss,
  className,
}: ErrorRecoveryBannerProps) {
  const [isResuming, setIsResuming] = useState(false);
  const [resumeError, setResumeError] = useState<string | null>(null);

  const errorInfo = ERROR_MESSAGES[errorCode] || {
    title: `Error ${errorCode}`,
    description: errorMessage || 'An error occurred. You can try resuming the task.',
  };

  const handleResume = async () => {
    setIsResuming(true);
    setResumeError(null);

    try {
      await onResume(taskId);
    } catch (error) {
      setResumeError(error instanceof Error ? error.message : 'Failed to resume task');
    } finally {
      setIsResuming(false);
    }
  };

  return (
    <Alert
      variant="destructive"
      className={cn(
        'border-amber-500/50 bg-amber-50 dark:bg-amber-950/30 text-amber-900 dark:text-amber-100',
        className
      )}
    >
      <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
      <AlertTitle className="text-amber-900 dark:text-amber-100 font-medium">
        {errorInfo.title}
      </AlertTitle>
      <AlertDescription className="mt-2">
        <p className="text-amber-800 dark:text-amber-200 text-sm">
          {errorInfo.description}
        </p>

        {resumeError && (
          <p className="mt-2 text-red-600 dark:text-red-400 text-sm">
            Resume failed: {resumeError}
          </p>
        )}

        <div className="mt-3 flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={handleResume}
            disabled={isResuming}
            className="bg-white dark:bg-zinc-900 hover:bg-amber-100 dark:hover:bg-amber-900/50 border-amber-300 dark:border-amber-700"
          >
            {isResuming ? (
              <>
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                Resuming...
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-3.5 w-3.5" />
                Resume Task
              </>
            )}
          </Button>

          {onDismiss && (
            <Button
              size="sm"
              variant="ghost"
              onClick={onDismiss}
              disabled={isResuming}
              className="text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/50"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>

        <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
          Task ID: {taskId.slice(0, 8)}...
        </p>
      </AlertDescription>
    </Alert>
  );
}
